import { useCallback, useMemo } from 'react';
import { Card } from '@amzn/stencil-react-components/card';
import { Col } from '@amzn/stencil-react-components/layout';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';

import {
  OptionSelect,
  type OptionSelectItem,
} from '../../../shared/ui/forms/OptionSelect';
import { OptionMultiSelect } from '../../../shared/ui/forms/OptionMultiSelect';
import { AuditScoreField } from '../../../shared/ui/forms/AuditScoreField';
import { FieldPair } from '../../../shared/ui/forms/FieldPair';
import { RequirementTogglesGrid } from './RequirementTogglesGrid';
import { ConfigField } from './ConfigField';
import { TagsConfigFields } from './TagsConfigFields';
import { useMessages } from '../../../shared/i18n/useMessages';
import { useCurrentUser } from '../../../shared/auth';
import { useDivisionsQuery } from '../../../shared/data/divisions/useDivisionsQuery';
import { useCategoriesQuery } from '../../../shared/data/categories/useCategoriesQuery';
import { useLocationsQuery } from '../../../shared/data/locations/useLocationsQuery';
import {
  AUDIT_SCORE_MAX,
  AUDIT_SCORE_MIN,
  CONFIG_BUNDLE,
  type ConfigKey,
  dtProps,
  nativeDtProps,
  NO_CONFIG_FIELD_ERRORS,
  type QuestionConfigCardProps,
} from './questionConfigShared';
/**
 * Build an option list from fetched `{ value, label }` pairs, guaranteeing
 * every `current` value still appears even if the fetch hasn't returned it
 * (a deactivated / cross-BU selection would otherwise vanish from its own
 * Select and read as "unset"). Fetched options win on label; missing current
 * values are appended labelled by their raw value. De-duped by value.
 */
function unionOptions(
  fetched: readonly OptionSelectItem[],
  current: readonly string[],
): OptionSelectItem[] {
  const byValue = new Map<string, OptionSelectItem>();
  for (const option of fetched) byValue.set(option.value, option);
  for (const value of current) {
    if (!byValue.has(value)) byValue.set(value, { value, label: value });
  }
  return Array.from(byValue.values());
}

/**
 * The "Basic Information" card of the Question Configuration step — overview,
 * business unit (read-only), division, category, audit score, tags, excluded
 * locations, source policy id (read-only), and the four Comment/Attachment
 * requirement toggles.
 *
 * Division, Category, Tags, and excluded Locations are wired to their real
 * option sources (Orbit/Helios) via React Query, scoped to the user's active
 * BU (Tags additionally by program). The single-select and flat multi-selects
 * union their option list with the draft's current selections so a deactivated
 * value never disappears; Tags is a grouped multi-select whose selection
 * round-trips through the draft's `TagMap`. Fully controlled via `patchConfig`.
 */
export function QuestionConfigBasicInformation({
  config,
  patchConfig,
  fieldErrors = NO_CONFIG_FIELD_ERRORS,
  disabled,
  dataTestId,
}: QuestionConfigCardProps): JSX.Element {
  const { t } = useMessages<ConfigKey>(CONFIG_BUNDLE);
  const infoAltText = t('configInfoAltText');

  // Option sources are scoped to the user's active BU (matches the manual
  // question flow, which scopes every option fetch by the logged-in user's
  // business unit — not the draft's policy-inherited `config.businessUnit`).
  // Each hook stays idle until the required scope is present.
  const { businessUnit, programId } = useCurrentUser();
  const businessUnitId = businessUnit ?? '';

  const { data: divisionsData } = useDivisionsQuery({
    businessUnitId,
    programId,
  });
  // Categories are scoped by business unit only — matching the manual question
  // flow, which lists all BU categories (`loadCategories({ businessUnitId,
  // isActive })`) and does NOT filter the list by division. Division is a
  // property on each category, not a list filter, so there is no
  // division→category cascade.
  const { data: categoriesData } = useCategoriesQuery(
    { businessUnitId, isActive: true },
    { enabled: !!businessUnitId },
  );
  const { data: locationsData } = useLocationsQuery({
    businessUnitId,
    isEnabledForBU: true,
  });
  // Fetched option lists, unioned with the draft's current selections so a
  // deactivated / cross-BU value the draft already holds never disappears
  // from its Select. Memoised for the reference-stable-options contract.
  const divisionOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (divisionsData?.divisions ?? []).map((d) => ({
          value: d.divisionName ?? '',
          label: d.divisionName ?? '',
        })),
        config.divisionName ? [config.divisionName] : [],
      ),
    [divisionsData, config.divisionName],
  );
  const categoryOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (categoriesData?.Categories ?? []).map((c) => ({
          value: c.categoryName ?? '',
          label: c.categoryName ?? '',
        })),
        config.categories,
      ),
    [categoriesData, config.categories],
  );
  const locationOptions = useMemo<readonly OptionSelectItem[]>(
    () =>
      unionOptions(
        (locationsData?.locations ?? []).map((loc) => ({
          value: loc.locationCode ?? '',
          label: loc.locationCode ?? '',
        })),
        config.locationsExcluded,
      ),
    [locationsData, config.locationsExcluded],
  );

  const handleDivisionChange = useCallback(
    (value: string) => patchConfig({ divisionName: value }),
    [patchConfig],
  );

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      {/* Full card width: related fields are paired into two-column rows
       *  (see below) to use the horizontal space and fit more within the
       *  page, rather than a single capped column. */}
      <Col gridGap="dimensions.spacing.400" width="100%">
        <H3>{t('configBasicHeading')}</H3>

        {/* Each ConfigField / Input / OptionMultiSelect / FieldPair below wraps
         *  a DIFFERENT control with unique props, options, and handlers — a
         *  config-array map would encode heterogeneous rendering and hurt
         *  readability without removing real duplication.
         *  standards-ignore: DRY:ConfigField
         *  standards-ignore: DRY:Input
         *  standards-ignore: DRY:OptionMultiSelect
         *  standards-ignore: DRY:FieldPair */}
        {/* Overview — required free text. */}
        <ConfigField
          label={t('configOverviewLabel')}
          info={t('configOverviewInfo')}
          infoAltText={infoAltText}
          isRequired
          {...(fieldErrors.overview ? { error: t('configOverviewRequiredError') } : {})}
          // renderLabel (not children) so the Input receives the error-text id
          // and can announce the error on focus via aria-describedby. The text
          // Input forwards the native aria-describedby to its <input>.
          renderLabel={({ errorId }) => (
            <Input
              value={config.overview}
              onChange={(e) => patchConfig({ overview: e.target.value })}
              placeholder={t('configOverviewPlaceholder')}
              disabled={disabled}
              aria-required
              aria-invalid={fieldErrors.overview}
              // ConfigField's label is a visual Text (not a <label>), so give
              // the plain Input its own accessible name from the same copy.
              aria-label={t('configOverviewLabel')}
              {...(errorId !== undefined ? { 'aria-describedby': errorId } : {})}
              {...nativeDtProps(dataTestId, 'overview')}
            />
          )}
        />

        {/* Related fields are paired into two-column rows (wrapping to stacked
         *  on narrow widths) so they use the horizontal space and fit within
         *  the page. Same Row + flex-column pattern as the requirement-toggle
         *  grid below. */}

        {/* Row: Business Unit | Division */}
        <FieldPair
          left={
            // Business Unit — inherited from the source policy, read-only.
            <ConfigField
              label={t('configBusinessUnitLabel')}
              info={t('configBusinessUnitInfo')}
              infoAltText={infoAltText}
            >
              <Input
                value={config.businessUnit}
                readOnly
                disabled={disabled}
                aria-label={t('configBusinessUnitLabel')}
                {...nativeDtProps(dataTestId, 'business-unit')}
              />
            </ConfigField>
          }
          right={
            // Division — required single-select (options from useDivisionsQuery).
            <ConfigField
              label={t('configDivisionLabel')}
              info={t('configDivisionInfo')}
              infoAltText={infoAltText}
              isRequired
              {...(fieldErrors.division
                ? { error: t('configDivisionRequiredError') }
                : {})}
            >
              <OptionSelect
                options={divisionOptions}
                value={config.divisionName || undefined}
                onChange={handleDivisionChange}
                placeholder={t('configDivisionPlaceholder')}
                disabled={disabled}
                isRequired
                ariaLabel={t('configDivisionLabel')}
                {...dtProps(dataTestId, 'division')}
              />
            </ConfigField>
          }
        />

        {/* Row: Category | Audit Score */}
        <FieldPair
          left={
            // Category — optional multi-select (options from useCategoriesQuery,
            // scoped by business unit).
            <ConfigField
              label={t('configCategoryLabel')}
              info={t('configCategoryInfo')}
              infoAltText={infoAltText}
            >
              <OptionMultiSelect
                options={categoryOptions}
                values={config.categories}
                onChange={(values) => patchConfig({ categories: values })}
                placeholder={t('configCategoryPlaceholder')}
                disabled={disabled}
                ariaLabel={t('configCategoryLabel')}
                {...dtProps(dataTestId, 'category')}
              />
            </ConfigField>
          }
          right={
            // Audit Score — required numeric dropdown (1–max).
            <ConfigField
              label={t('configAuditScoreLabel')}
              info={t('configAuditScoreInfo')}
              infoAltText={infoAltText}
              isRequired
              {...(fieldErrors.auditScore
                ? { error: t('configAuditScoreRequiredError') }
                : {})}
              renderLabel={() => (
                <AuditScoreField
                  value={config.auditScore}
                  onChange={(value) => patchConfig({ auditScore: value })}
                  min={AUDIT_SCORE_MIN}
                  max={AUDIT_SCORE_MAX}
                  selectAriaLabel={t('configAuditScoreSelectAriaLabel')}
                  disabled={disabled}
                  {...dtProps(dataTestId, 'audit-score')}
                />
              )}
            />
          }
        />

        {/* Tags Included / Tags Excluded (cross-fed grouped selects) and then
         *  Locations Excluded stack vertically — one field after another. */}
        <TagsConfigFields
          config={config}
          patchConfig={patchConfig}
          fieldErrors={fieldErrors}
          disabled={disabled}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />

        {/* Locations Excluded — a multi-select (options from useLocationsQuery)
         *  that owns both add and remove; its built-in tags show the current
         *  exclusions, so no separate chip list. */}
        <ConfigField
          label={t('configLocationsExcludedLabel')}
          info={t('configLocationsExcludedInfo')}
          infoAltText={infoAltText}
        >
          <OptionMultiSelect
            options={locationOptions}
            values={config.locationsExcluded}
            onChange={(values) => patchConfig({ locationsExcluded: values })}
            placeholder={t('configLocationsExcludedPlaceholder')}
            disabled={disabled}
            ariaLabel={t('configLocationsExcludedLabel')}
            {...dtProps(dataTestId, 'locations')}
          />
        </ConfigField>

        {/* Source Policy ID — auto-populated (WHIZ-002 AC7), read-only. */}
        <ConfigField
          label={t('configSourcePolicyLabel')}
          info={t('configSourcePolicyInfo')}
          infoAltText={infoAltText}
        >
          {config.sourcePolicyId ? (
            <Input
              value={config.sourcePolicyId}
              readOnly
              disabled={disabled}
              aria-label={t('configSourcePolicyLabel')}
              {...nativeDtProps(dataTestId, 'source-policy')}
            />
          ) : (
            <Text
              color="color.text.secondary"
              {...nativeDtProps(dataTestId, 'source-policy-empty')}
            >
              {t('configSourcePolicyEmpty')}
            </Text>
          )}
        </ConfigField>

        {/* Requirement toggles — 2×2 grid (Comment | Attachment columns; Fail
         *  row then Pass row), matching the manual question flow. */}
        <RequirementTogglesGrid
          config={config}
          patchConfig={patchConfig}
          disabled={disabled}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />
      </Col>
    </Card>
  );
}
